import { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix default-marker icon path when Leaflet is bundled with webpack/craco.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const FitContent = ({ points, route }) => {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return;
    if (route && route.length > 1) {
      const bounds = L.latLngBounds(route);
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 12, animate: false });
      return;
    }
    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lon], 12, { animate: false });
      return;
    }
    const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lon]));
    map.fitBounds(bounds, { padding: [50, 50], maxZoom: 12, animate: false });
  }, [points, route, map]);
  return null;
};

const milesFromMeters = (m) => Math.round((m / 1609.34) * 10) / 10;
const minutesFromSeconds = (s) => Math.round(s / 60);

/**
 * Full-bleed Leaflet canvas. Receives the OSRM `route` from the parent hook
 * (no internal fetch) so the schedule + map share the exact same data.
 */
const AiSearchMap = ({ mapped, route, routeError }) => {
  const [mapReady, setMapReady] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setMapReady(true), 60);
    return () => {
      clearTimeout(t);
      setMapReady(false);
    };
  }, []);

  const validPoints = (mapped || []).filter(
    (p) => typeof p?.lat === 'number' && typeof p?.lon === 'number',
  );

  const fallbackLine =
    validPoints.length >= 2 ? validPoints.map((p) => [p.lat, p.lon]) : null;

  const mapCenter = validPoints.length > 0
    ? [validPoints[0].lat, validPoints[0].lon]
    : [38.5025, -122.2654]; // Napa fallback

  return (
    <div
      className="relative w-full h-full rounded-2xl overflow-hidden border border-white/10 shadow-[0_25px_60px_-20px_rgba(0,0,0,0.6)]"
      style={{ isolation: 'isolate', zIndex: 0 }}
      data-testid="ai-search-map-wrap"
    >
      {route && (
        <div
          className="absolute top-3 left-3 z-[1000] inline-flex items-center gap-2 bg-[#1a1a2e]/90 backdrop-blur-sm text-white text-[11px] font-semibold rounded-full px-3 py-1.5 shadow-lg border border-white/10"
          data-testid="route-summary-pill"
        >
          <span className="w-1.5 h-1.5 bg-gold-400 rounded-full animate-pulse" />
          {milesFromMeters(route.distance_m)} mi · {minutesFromSeconds(route.duration_s)} min drive
        </div>
      )}

      {!route && routeError && fallbackLine && (
        <div
          className="absolute top-3 left-3 z-[1000] inline-flex items-center gap-2 bg-[#1a1a2e]/80 backdrop-blur-sm text-white/80 text-[11px] font-medium rounded-full px-3 py-1.5 shadow-lg border border-white/10"
          data-testid="route-summary-fallback"
        >
          <span className="w-1.5 h-1.5 bg-white/50 rounded-full" />
          Estimated route
        </div>
      )}

      {mapReady ? (
        <MapContainer
          center={mapCenter}
          zoom={9}
          scrollWheelZoom
          style={{ height: '100%', width: '100%', background: '#0c1226' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <FitContent points={validPoints} route={route?.coords} />

          {/* Solid base line (always visible) + animated dashed flow on top for "live route" feel. */}
          {route?.coords && (
            <>
              <Polyline
                positions={route.coords}
                pathOptions={{
                  color: '#8c1e37',
                  weight: 6,
                  opacity: 1,
                  className: 'wf-route-line',
                }}
              />
              <Polyline
                positions={route.coords}
                pathOptions={{
                  color: '#f4c542',
                  weight: 3,
                  opacity: 0.95,
                  className: 'wf-route-line-glow',
                }}
              />
            </>
          )}

          {!route?.coords && fallbackLine && (
            <Polyline
              positions={fallbackLine}
              pathOptions={{
                color: '#d4af50',
                weight: 3,
                opacity: 0.7,
                dashArray: '4 8',
                className: 'wf-route-line-fallback',
              }}
            />
          )}

          {validPoints.map((r, i) => (
            <Marker key={`${r.name}-${i}-${r.lat},${r.lon}`} position={[r.lat, r.lon]}>
              <Popup>
                <div className="space-y-1" style={{ minWidth: 200 }}>
                  {r.editorial_badge ? (
                    <div
                      data-testid={`map-popup-editorial-badge-${i}`}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        padding: '2px 8px',
                        background: 'linear-gradient(90deg, #fef3c7 0%, #fde68a 50%, #fef3c7 100%)',
                        border: '1px solid #d4af50',
                        borderRadius: 9999,
                        fontSize: 9,
                        fontWeight: 700,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        color: '#5b1e2a',
                      }}
                      title="Editorial highlight, hand-picked by the AI Concierge"
                    >
                      <span style={{ color: '#a77a17' }}>✦</span>
                      {r.editorial_badge}
                    </div>
                  ) : null}
                  <div style={{ fontWeight: 700, fontSize: 14, marginTop: r.editorial_badge ? 4 : 0 }}>{r.name}</div>
                  <div style={{ fontSize: 12, color: '#444' }}>{r.vibe}</div>
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(r.address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      color: '#1a73e8',
                      fontWeight: 600,
                      fontSize: 12,
                    }}
                  >
                    Directions →
                  </a>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      ) : (
        <div className="h-full w-full flex items-center justify-center bg-[#0c1226]">
          <div className="text-white/40 text-xs">Loading map…</div>
        </div>
      )}
    </div>
  );
};

export default AiSearchMap;
